/**
 * BL-180 Gate 1 — Accounting Adapter Interface
 *
 * Defines the shared contract that both the QuickBooks Online (QBO) and Xero
 * connectors must satisfy.  Gates 2–5 will each ship a concrete implementation
 * of this interface.  This module also exports the platform constants, status
 * values, and entity-type values shared across the accounting feature.
 *
 * IMPORTANT: This module contains no I/O or SDK calls.  It defines shapes and
 * pure helper functions only — safe to import in any context (browser, edge
 * function, test runner).
 */

// ── Platform constants ────────────────────────────────────────────────────────

export const ACCOUNTING_PLATFORMS = /** @type {const} */ ({
  QBO: 'qbo',
  XERO: 'xero',
});

export const PLATFORM_LABELS = {
  [ACCOUNTING_PLATFORMS.QBO]: 'QuickBooks Online',
  [ACCOUNTING_PLATFORMS.XERO]: 'Xero',
};

// ── Connection status constants ───────────────────────────────────────────────

export const CONNECTION_STATUS = /** @type {const} */ ({
  PENDING: 'pending',
  ACTIVE: 'active',
  TOKEN_EXPIRED: 'token_expired',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
});

// ── Entity type constants (must mirror the DB CHECK constraint) ───────────────

export const ACCOUNTING_ENTITY_TYPES = /** @type {const} */ ({
  ACCOUNT: 'account',
  INVOICE: 'invoice',
  PAYMENT: 'payment',
  CHART_ACCOUNT: 'chart_account',
});

// ── Sync direction constants ──────────────────────────────────────────────────

export const SYNC_DIRECTIONS = /** @type {const} */ ({
  PUSH_ONLY: 'push_only',
  PULL_ONLY: 'pull_only',
  BIDIRECTIONAL: 'bidirectional',
});

// ── Connection helpers ────────────────────────────────────────────────────────

/**
 * Returns true when a connection row has an actionable status.
 * A connection that is PENDING has started the OAuth flow but is not yet usable
 * for sync.  TOKEN_EXPIRED and ERROR both require user action before sync can
 * proceed.
 *
 * @param {{ status: string }} connection
 * @returns {boolean}
 */
export function isConnectionActive(connection) {
  return connection?.status === CONNECTION_STATUS.ACTIVE;
}

/**
 * Returns true when the connection requires the user to re-authenticate.
 *
 * @param {{ status: string }} connection
 * @returns {boolean}
 */
export function requiresReconnect(connection) {
  return (
    connection?.status === CONNECTION_STATUS.TOKEN_EXPIRED ||
    connection?.status === CONNECTION_STATUS.ERROR
  );
}

/**
 * Returns a human-readable status label for display in the UI.
 *
 * @param {{ status: string }} connection
 * @returns {string}
 */
export function getConnectionStatusLabel(connection) {
  const labels = {
    [CONNECTION_STATUS.PENDING]: 'Connecting…',
    [CONNECTION_STATUS.ACTIVE]: 'Connected',
    [CONNECTION_STATUS.TOKEN_EXPIRED]: 'Reconnect needed',
    [CONNECTION_STATUS.DISCONNECTED]: 'Disconnected',
    [CONNECTION_STATUS.ERROR]: 'Sync error',
  };
  return labels[connection?.status] ?? 'Unknown';
}

// ── Entity-mapping helpers ────────────────────────────────────────────────────

/**
 * Builds a new entity-mapping row ready for INSERT.  The `id` and `created_at`
 * fields are intentionally omitted — the database DEFAULT handles them.
 *
 * @param {{
 *   orgId: string,
 *   connectionId: string,
 *   entityType: string,
 *   entityId: string,
 *   externalId: string,
 *   externalDisplayName?: string,
 *   syncDirection?: string,
 * }} params
 * @returns {object}
 */
export function buildEntityMapping({
  orgId,
  connectionId,
  entityType,
  externalId,
  entityId,
  externalDisplayName = null,
  syncDirection = SYNC_DIRECTIONS.BIDIRECTIONAL,
}) {
  if (!Object.values(ACCOUNTING_ENTITY_TYPES).includes(entityType)) {
    throw new Error(
      `Invalid entity_type "${entityType}". Must be one of: ${Object.values(ACCOUNTING_ENTITY_TYPES).join(', ')}`
    );
  }
  if (!Object.values(SYNC_DIRECTIONS).includes(syncDirection)) {
    throw new Error(
      `Invalid sync_direction "${syncDirection}". Must be one of: ${Object.values(SYNC_DIRECTIONS).join(', ')}`
    );
  }
  return {
    org_id: orgId,
    connection_id: connectionId,
    entity_type: entityType,
    entity_id: entityId,
    external_id: externalId,
    external_display_name: externalDisplayName,
    sync_direction: syncDirection,
    has_conflict: false,
    conflict_detected_at: null,
    conflict_notes: null,
    last_synced_at: null,
  };
}

/**
 * Marks an entity mapping as having a conflict that requires human review.
 * Returns a new object — does not mutate the input.
 *
 * @param {object} mapping  Existing mapping row.
 * @param {string} notes    Human-readable description of the conflict.
 * @returns {object}
 */
export function flagMappingConflict(mapping, notes) {
  return {
    ...mapping,
    has_conflict: true,
    conflict_detected_at: new Date().toISOString(),
    conflict_notes: notes ?? 'Conflicting edits detected — pending review.',
  };
}

/**
 * Clears a conflict flag after it has been resolved by a human reviewer.
 * Returns a new object — does not mutate the input.
 *
 * @param {object} mapping  Existing mapping row.
 * @returns {object}
 */
export function clearMappingConflict(mapping) {
  return {
    ...mapping,
    has_conflict: false,
    conflict_detected_at: null,
    conflict_notes: null,
  };
}

// ── Sync-log helpers ──────────────────────────────────────────────────────────

/**
 * Builds an immutable sync-log row ready for INSERT.
 * The `id` and `performed_at` fields are handled by DB DEFAULTs.
 *
 * @param {{
 *   orgId: string,
 *   connectionId: string,
 *   entityType: string,
 *   entityId?: string,
 *   direction: 'push' | 'pull',
 *   success: boolean,
 *   errorMessage?: string,
 *   rawResponse?: object,
 * }} params
 * @returns {object}
 */
export function buildSyncLogEntry({
  orgId,
  connectionId,
  entityType,
  entityId = null,
  direction,
  success,
  errorMessage = null,
  rawResponse = null,
}) {
  if (!['push', 'pull'].includes(direction)) {
    throw new Error(`Invalid direction "${direction}". Must be "push" or "pull".`);
  }
  return {
    org_id: orgId,
    connection_id: connectionId,
    entity_type: entityType,
    entity_id: entityId,
    direction,
    success,
    error_message: errorMessage,
    raw_response: rawResponse,
  };
}

// ── Adapter interface contract (JSDoc only — enforced by concrete impls) ─────
//
// Each concrete adapter (QBO, Xero) must export an object conforming to:
//
//   {
//     platform: ACCOUNTING_PLATFORMS.QBO | ACCOUNTING_PLATFORMS.XERO,
//
//     // Build the OAuth authorisation URL that starts the connection flow.
//     buildAuthUrl(orgId: string, redirectUri: string, state: string): string,
//
//     // Exchange an authorisation code for tokens.  Returns the connection row
//     // update (external_tenant_id, status: 'active').  Token storage is
//     // handled inside the Edge Function — not returned here.
//     exchangeCode(code: string, redirectUri: string): Promise<{ externalTenantId: string }>,
//
//     // Push a single IntelAgent entity to the external ledger.
//     // Returns the external_id assigned by the platform.
//     pushEntity(connection: object, entityType: string, payload: object): Promise<string>,
//
//     // Pull updates from the external ledger for a given entity.
//     pullEntity(connection: object, entityType: string, externalId: string): Promise<object>,
//
//     // Verify the connection is still live (token valid, platform reachable).
//     verifyConnection(connection: object): Promise<boolean>,
//   }
//
// This comment block is the Gate 1 contract.  Gates 2–5 implement it.
